import { useEmailAttachments } from '@/hooks/use-email-attachments';
import { plainTextToLexicalJson } from '@/libs/utils/lexical-utils';
import { Field } from '@/components/ui/field';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Box, createListCollection, HStack, Input, Stack, Text, VStack } from '@chakra-ui/react';
import {
  TemplateChannel,
  TemplateContext,
  getTemplateVariableErrors,
  templateContentMaxLength,
  templateContentWarnLength,
} from '@fluenceur/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChatLines, Instagram, Mail, Megaphone, Suitcase, UserPlus } from 'iconoir-react';
import type { EditorState } from 'lexical';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmailInvitationMessageEditor } from '@/components/editor/lexical/email-invitation-message-editor';
import { LanguageFlag } from '@/components/language/LanguageFlag';
import { availableVariablesForPair, sampleValuesForPair } from './template-variables';
import { contextsForChannel, reconcilePair, supportedChannels } from './template-pairs';
import {
  channelLabelKey,
  contextLabelKey,
  languageLabelKey,
  renderTemplatePreview,
} from './template-library-utils';
import {
  TEMPLATE_LIBRARY_LANGUAGES,
  countTemplateCharacters,
  type TemplateFormValues,
} from './template-form-schema';
import { templateFormSchema } from './template-form-schema';

const CHANNELS = supportedChannels();
const LANGUAGES = TEMPLATE_LIBRARY_LANGUAGES;
const TEMPLATE_EDITOR_MAX_HEIGHT = 'clamp(180px, 42vh, 420px)';

const ICON_SIZE = 16;

/** iconoir icon per channel, shown in the channel select options. */
const CHANNEL_ICON: Record<TemplateChannel, ReactNode> = {
  [TemplateChannel.EMAIL]: <Mail width={ICON_SIZE} height={ICON_SIZE} />,
  [TemplateChannel.INSTAGRAM_DM]: <Instagram width={ICON_SIZE} height={ICON_SIZE} />,
  [TemplateChannel.INSTAGRAM_COMMENT]: <ChatLines width={ICON_SIZE} height={ICON_SIZE} />,
};

/** iconoir icon per context, shown in the context select options. */
const CONTEXT_ICON: Record<TemplateContext, ReactNode> = {
  [TemplateContext.INVITATION]: <UserPlus width={ICON_SIZE} height={ICON_SIZE} />,
  [TemplateContext.GENERIC]: <Megaphone width={ICON_SIZE} height={ICON_SIZE} />,
  [TemplateContext.COLLAB]: <Suitcase width={ICON_SIZE} height={ICON_SIZE} />,
};

interface TemplateFormProps {
  /** Initial values. Edit mode passes the existing entry; create passes defaults. */
  initialValues: TemplateFormValues;
  /**
   * When true the (channel, context, language) selectors are locked. Always
   * locked in edit mode because the backend treats them as immutable.
   */
  lockPair: boolean;
  /** Stable key that changes when the edited template changes (re-seeds editor). */
  formKey: string | number;
  onSubmit: (values: TemplateFormValues) => void | Promise<void>;
  /** Bound to an external submit button via the `formId`. */
  formId: string;
  onAttachmentBlockedChange?: (blocked: boolean) => void;
}

/**
 * Autonomous, reusable template form: pair/language selectors, name, the
 * generalized Lexical editor fed by the pair's variables, a "set as default"
 * toggle and a live sample preview. Owns its own React Hook Form + Zod state and
 * reports completed values through `onSubmit`. Designed to be embedded both in
 * the Settings drawer and the future send modal, so it renders no chrome of its
 * own (no header, no submit button): the host provides those and triggers
 * submission through `formId`.
 */
export function TemplateForm({
  initialValues,
  lockPair,
  formKey,
  onSubmit,
  formId,
  onAttachmentBlockedChange,
}: TemplateFormProps): ReactElement {
  const { t } = useTranslation();
  // Editor remount counter. Kept in state (not a ref) so bumping it actually
  // re-renders and forces LexicalComposer to remount with fresh content; a ref
  // mutation would leave the editor mounted with stale, submittable content.
  const [editorKey, setEditorKey] = useState(0);
  const contentErrorRef = useRef<HTMLSpanElement>(null);
  const files = useEmailAttachments({ initialAttachments: initialValues.attachments });
  const { resetAttachments, replaceAttachments, isBusy: attachmentsBusy } = files;

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
    formState: { errors },
  } = useForm<TemplateFormValues>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  // Re-seed the whole form (and remount the editor) when the host swaps the
  // template being edited or duplicated.
  useEffect(() => {
    reset(initialValues);
    resetAttachments(initialValues.attachments ?? []);
    setEditorKey((previous) => previous + 1);
    // initialValues is recomputed by the host per template; formKey is the
    // stable identity trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, reset, resetAttachments]);

  useEffect(() => {
    setValue('attachments', files.attachments);
  }, [files.attachments, setValue]);
  useEffect(() => {
    onAttachmentBlockedChange?.(files.isBlocked);
  }, [files.isBlocked, onAttachmentBlockedChange]);
  const channel = watch('channel');
  const context = watch('context');
  const content = watch('content');
  const contentMaxLength = useMemo(() => templateContentMaxLength(channel), [channel]);
  const contentWarnLength = useMemo(() => templateContentWarnLength(channel), [channel]);
  const contentCharacterCount = useMemo(() => countTemplateCharacters(content ?? ''), [content]);
  const contentCounterColor =
    contentCharacterCount > contentMaxLength
      ? 'red.600'
      : contentCharacterCount >= contentWarnLength
        ? 'orange.600'
        : 'fg.muted';

  const availableVariables = useMemo(
    () => availableVariablesForPair(channel, context),
    [channel, context]
  );
  const requiredVariables = availableVariables
    .filter((variable) => variable.required)
    .map((variable) => `{{${variable.key}}}`)
    .join(', ');

  // Interpreted preview: the message with its variables replaced by sample
  // values, mirroring the outreach modals (show the rendered content, nothing more).
  const previewText = useMemo(
    () => renderTemplatePreview(content, sampleValuesForPair(channel, context)),
    [content, channel, context]
  );

  const contextOptions = useMemo(() => contextsForChannel(channel), [channel]);

  const channelCollection = useMemo(
    () =>
      createListCollection({
        items: CHANNELS.map((value) => ({ value, label: t(channelLabelKey(value)) })),
      }),
    [t]
  );
  const contextCollection = useMemo(
    () =>
      createListCollection({
        items: contextOptions.map((value) => ({ value, label: t(contextLabelKey(value)) })),
      }),
    [t, contextOptions]
  );
  const languageCollection = useMemo(
    () =>
      createListCollection({
        items: LANGUAGES.map((value) => ({ value, label: t(languageLabelKey(value)) })),
      }),
    [t]
  );

  // Changing one axis can invalidate the pair; reconcile so the form never
  // holds an unsupported (channel, context) combination. The selector the user
  // touched is the anchor and is preserved.
  const handleChannelChange = useCallback(
    async (nextChannel: TemplateChannel) => {
      const reconciled = reconcilePair(nextChannel, context, 'channel');
      if (attachmentsBusy) return;
      if (reconciled.channel !== TemplateChannel.EMAIL && !(await replaceAttachments([]))) return;
      setValue('channel', reconciled.channel, { shouldDirty: true, shouldValidate: true });
      setValue('context', reconciled.context, { shouldDirty: true, shouldValidate: true });
      await trigger('content');
    },
    [context, setValue, trigger, attachmentsBusy, replaceAttachments]
  );

  const handleContextChange = useCallback(
    async (nextContext: TemplateContext) => {
      const reconciled = reconcilePair(channel, nextContext, 'context');
      if (attachmentsBusy) return;
      if (reconciled.channel !== TemplateChannel.EMAIL && !(await replaceAttachments([]))) return;
      setValue('channel', reconciled.channel, { shouldDirty: true, shouldValidate: true });
      setValue('context', reconciled.context, { shouldDirty: true, shouldValidate: true });
      await trigger('content');
    },
    [channel, setValue, trigger, attachmentsBusy, replaceAttachments]
  );

  const editorInitialContent = useMemo(() => {
    if (initialValues.contentLexicalJson) {
      return JSON.stringify(initialValues.contentLexicalJson);
    }
    return initialValues.content ? plainTextToLexicalJson(initialValues.content) : null;
    // Only depends on the seeded template, not on live edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const handleEditorChange = useCallback(
    (editorState: EditorState, plainText: string) => {
      setValue('content', plainText, { shouldValidate: true, shouldDirty: true });
      setValue('contentLexicalJson', editorState.toJSON() as unknown as Record<string, unknown>, {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const contentErrorText = useMemo(() => {
    if (!errors.content?.message) {
      return undefined;
    }
    const { invalidVariables, missingRequiredVariables } = getTemplateVariableErrors(
      channel,
      context,
      content ?? ''
    );
    const variables =
      errors.content.message === 'templates.form.errors.invalidVariables'
        ? invalidVariables
        : missingRequiredVariables;
    return t(errors.content.message, {
      max: contentMaxLength.toLocaleString(),
      variables: variables.map((key) => `{{${key}}}`).join(', '),
    });
  }, [channel, context, content, contentMaxLength, errors.content?.message, t]);

  useEffect(() => {
    if (contentErrorText && !errors.name) {
      contentErrorRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [contentErrorText, errors.name]);

  return (
    <Box
      as="form"
      id={formId}
      onSubmit={handleSubmit((values) => {
        if (!files.isBlocked) return onSubmit(values);
      })}
      display="flex"
      flex={1}
      flexDirection="column"
      minH={0}
    >
      <VStack align="stretch" gap={5} flex={1} minH={0}>
        <Field
          label={t('templates.form.name')}
          errorText={errors.name ? t(errors.name.message ?? '') : undefined}
          invalid={!!errors.name}
        >
          <Input
            variant="outline"
            placeholder={t('templates.form.namePlaceholder')}
            {...register('name')}
          />
        </Field>

        <Stack direction={{ base: 'column', md: 'row' }} gap={4}>
          <Field label={t('templates.form.channel')} flex={1}>
            <Controller
              control={control}
              name="channel"
              render={({ field }) => (
                <SelectRoot
                  collection={channelCollection}
                  value={[field.value]}
                  onValueChange={(e) => {
                    const next = e.value[0];
                    if (next) void handleChannelChange(next as TemplateChannel);
                  }}
                  disabled={lockPair || files.isBusy}
                >
                  <SelectTrigger>
                    <SelectValueText />
                  </SelectTrigger>
                  <SelectContent>
                    {channelCollection.items.map((item) => (
                      <SelectItem key={item.value} item={item}>
                        <HStack gap={2}>
                          {CHANNEL_ICON[item.value]}
                          <span>{item.label}</span>
                        </HStack>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
            />
          </Field>

          <Field label={t('templates.form.context')} flex={1}>
            <Controller
              control={control}
              name="context"
              render={({ field }) => (
                <SelectRoot
                  collection={contextCollection}
                  value={[field.value]}
                  onValueChange={(e) => {
                    const next = e.value[0];
                    if (next) void handleContextChange(next as TemplateContext);
                  }}
                  disabled={lockPair || files.isBusy}
                >
                  <SelectTrigger>
                    <SelectValueText />
                  </SelectTrigger>
                  <SelectContent>
                    {contextCollection.items.map((item) => (
                      <SelectItem key={item.value} item={item}>
                        <HStack gap={2}>
                          {CONTEXT_ICON[item.value]}
                          <span>{item.label}</span>
                        </HStack>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
            />
          </Field>

          <Field label={t('templates.form.language')} flex={1}>
            <Controller
              control={control}
              name="language"
              render={({ field }) => (
                <SelectRoot
                  collection={languageCollection}
                  value={[field.value]}
                  onValueChange={(e) => {
                    const next = e.value[0];
                    if (next) field.onChange(next);
                  }}
                  disabled={lockPair || files.isBusy}
                >
                  <SelectTrigger>
                    <SelectValueText />
                  </SelectTrigger>
                  <SelectContent>
                    {languageCollection.items.map((item) => (
                      <SelectItem key={item.value} item={item}>
                        <HStack gap={2}>
                          <LanguageFlag language={item.value} />
                          <span>{item.label}</span>
                        </HStack>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
            />
          </Field>
        </Stack>

        <Field
          label={t('templates.form.content')}
          helperText={
            requiredVariables
              ? t('templates.form.requiredVariablesHint', { variables: requiredVariables })
              : undefined
          }
          errorText={
            contentErrorText ? <span ref={contentErrorRef}>{contentErrorText}</span> : undefined
          }
          invalid={!!errors.content}
          display="flex"
          flex={1}
          flexDirection="column"
          minH={0}
        >
          <EmailInvitationMessageEditor
            attachmentControl={
              channel === TemplateChannel.EMAIL
                ? {
                    attachments: files.attachments,
                    onFileSelect: files.handleFileSelect,
                    onRemoveAttachment: files.handleRemoveAttachment,
                    isUploading: files.isUploading,
                    deletingId: files.deletingId,
                    issues: files.issues,
                    onDismissIssue: files.dismissIssue,
                    validationError: files.validationError,
                    maxTotalBytes: files.maxTotalBytes,
                  }
                : undefined
            }
            key={editorKey}
            initialContent={editorInitialContent}
            availableVariables={availableVariables}
            onContentChange={handleEditorChange}
            showHeader={false}
            placeholder={t('templates.form.contentPlaceholder')}
            insertVariableLabel={t('templates.form.insertVariable')}
            ariaLabel={t('templates.form.content')}
            previewLabel={t('templates.form.previewLabel')}
            previewContent={
              <Text fontSize="sm" whiteSpace="pre-wrap" color="fg">
                {previewText}
              </Text>
            }
            footerTrailing={
              <Text fontSize="sm" color={contentCounterColor} whiteSpace="nowrap">
                {contentCharacterCount.toLocaleString()} / {contentMaxLength.toLocaleString()}
              </Text>
            }
            editorMaxH={TEMPLATE_EDITOR_MAX_HEIGHT}
          />
        </Field>

        <Controller
          control={control}
          name="isDefault"
          render={({ field }) => (
            <HStack align="flex-start" gap={3}>
              <Checkbox
                mt="2px"
                colorPalette="primary"
                checked={field.value}
                onCheckedChange={(details) => field.onChange(!!details.checked)}
                aria-label={t('templates.form.setAsDefault')}
              />
              <Stack gap={0.5} cursor="pointer" onClick={() => field.onChange(!field.value)}>
                <Text fontWeight="medium" lineHeight="1.2">
                  {t('templates.form.setAsDefault')}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {t('templates.form.setAsDefaultHint')}
                </Text>
              </Stack>
            </HStack>
          )}
        />
      </VStack>
    </Box>
  );
}
